import { Operation, OperationChainLink, RiseChain, Scenario } from '@/types';

export type DisplayDataType = number | string | boolean;
export type ViewData = Record<string, DisplayDataType>;

export type DataViewProvider<ArgsType> = {
  args: ArgsType;
  providerFunction: (args: ArgsType) => ViewData[];
};

export const getDataView = ({
  providers,
}: {
  providers: Array<DataViewProvider<any>>;
}): ViewData[] => {
  return providers.flatMap((provider) => {
    const { args, providerFunction } = provider;
    return providerFunction(args);
  });
};

export const comprehensiveView = ({ scenario }: { scenario: Scenario }) => {
  if (!scenario) return [];

  const data = scenario.operation_chains.flatMap((chain) => {
    const receiptOp = scenario.operations.find(
      (op) => op.id === chain.operations[0],
    );
    const deliveryOp = scenario.operations.find(
      (op) => op.id === chain.operations[chain.operations.length - 1],
    );

    if (!receiptOp || !deliveryOp) {
      return [];
    }

    const chainOps = chain.chain_links
      .map((chain_link) => {
        const operation = scenario.operations.find(
          (op) => op.id === chain_link.operation_id,
        );
        if (!operation) {
          return null;
        }
        return {
          ...operation,
          chain_link,
        } as Operation & { chain_link: OperationChainLink };
      })
      .filter((op) => op !== null);

    const operationData = chainOps.map((op) => {
      const FAKE_FUEL_COST = 3;
      const variableCost = Number(op.transport_cost_per_dth) * op.received_dth;
      const fuelCost =
        Number(op.fuel_factor - 1) * FAKE_FUEL_COST * op.received_dth;
      const transportCost = variableCost + fuelCost;
      const firstOp = op.chain_link.order == 0;
      return {
        // Operation
        'Operation ID': op.id,
        'Operation Delivery TSP': op.delivery_point.tsp_name,
        'Operation Delivery Location': op.delivery_point.loc_name,
        'Operation Receipt TSP': op.receipt_point.tsp_name,
        'Operation Receipt Location': op.receipt_point.loc_name,
        'Operation Delivery Description': op.delivery_description,
        'Operation Receipt Description': op.receipt_description,
        Contract: `(${op.contract_k_id}) ${op.contract_name}`,
        'Operation Variable Cost': variableCost,
        'Operation Fuel Cost': fuelCost,
        'Operation Transport Cost': transportCost,
        'Contract Priority': op.transport_priority,
        'Intra TSP':
          op.delivery_point.tsp_name === op.receipt_point.tsp_name
            ? 'Yes'
            : 'No',
        'Operation Delivery Asset Type': op.delivery_asset_type,
        'Operation Receipt Asset Type': op.receipt_asset_type,
        'Delivery Asset': op.delivery_asset_name,
        'Receipt Asset': op.receipt_asset_name,
        'Operation Dth': Number(op.delivered_dth).toFixed(0),

        // Chain
        'Chain ID': chain.id,
        'Chain Name': chain.chain_info?.name ?? 'Unset',
        'Chain Info ID': chain.chain_info?.id ?? 'Unset',
        'Chain Delivery TSP': deliveryOp.delivery_point.tsp_name,
        'Chain Delivery Location': deliveryOp.delivery_point.loc_name,
        'Chain Delivery Description': deliveryOp.delivery_description,
        'Chain Receipt TSP': receiptOp.receipt_point.tsp_name,
        'Chain Receipt Location': deliveryOp.receipt_point.loc_name,
        'Chain Receipt Description': receiptOp.receipt_description,
        'Chain Delivery Asset': deliveryOp.delivery_asset_name,
        'Chain Receipt Asset': receiptOp.receipt_asset_name,

        // I grabbed the data from the first op of each chain to prevent us from counting volume or profit multiple times
        // This could be done in some other ways, like having separate data for chains and operations
        // Or adding operation index and having the presets select the ops with index 0 when we want chain based data
        'Chain Delivered Dth': firstOp
          ? Number(chain.delivered_dth).toFixed(0)
          : null,
        // chain doesn't have a received_dth, so I'm using the first op's received_dth
        'Chain Received Dth': firstOp
          ? Number(op.received_dth).toFixed(0)
          : null,
        'Chain Profit': firstOp ? Number(chain.profit).toFixed(0) : null,
        'Using Market': [op.delivery_asset_type, op.receipt_asset_type].some(
          (type) => ['MARKET'].includes(type),
        ),
        'Operation Origin': 'Labs',
      } as ViewData;
    });
    return operationData;
  });

  return data;
};

export const riseChainView = ({
  transports = [],
}: {
  transports: RiseChain[];
}): ViewData[] => {
  return transports.flatMap((transport) => {
    const variableCost = Number(transport.chain_transport_cost);
    const fuelCost =
      (Number(transport.chain_received_dth) -
        Number(transport.chain_delivered_dth)) *
      Number(transport.chain_receipt_price);
    const transportCost = variableCost + fuelCost;
    return transport.operation_links.map((operation): ViewData => {
      const firstOp = operation.link_order == 0;
      return {
        'Chain ID': transport.chain_id,
        'Chain Delivery TSP': transport.chain_delivery_tsp,
        'Chain Delivery Location': transport.chain_delivery_point_name,
        'Chain Receipt TSP': transport.chain_receipt_tsp,
        'Chain Receipt Location': transport.chain_receipt_point_name,
        'Chain Dth': transport.chain_received_dth,
        'Chain Variable Cost': variableCost,
        'Chain Fuel Cost': fuelCost,
        'Chain Transport Cost': transportCost,
        'Chain Profit': firstOp
          ? Number(transport.chain_profit).toFixed(0)
          : '',
        Contract: `(${operation.operation_contract_num}) ${operation.operation_transport_contract}`,
        'Operation ID': operation.operation_id,
        'Operation Dth': operation.link_received_dth,
        'Operation Delivery TSP': operation.operation_delivery_tsp,
        'Operation Delivery Location': operation.operation_delivery_point_name,
        'Operation Receipt TSP': operation.operation_receipt_tsp,
        'Operation Receipt Location': operation.operation_receipt_point_name,
        'Operation Receipt Asset Type': 'Rise Transport',
        'Operation Delivery Asset Type': 'Rise Transport',
        'Operation Origin': 'Energy',
        'Using Market': 'No',
      };
    });
  });
};
